import { observable, computed, toJS, makeObservable } from 'mobx'
import { TILE_CONTENT } from '@mixtiles/web-backend-shared'
import {
  type Photo,
  type ThumbDimensions,
} from 'components/PhotoBank/PhotoBank.types'
import { isExpiredPhoto } from 'components/PhotoBank/photoUtils'
import { BaseTileStore } from './BaseTileStore'
import type { PhotoBankStore } from '../../photoBankStore/PhotoBankStore'
import type { SinglePhotoBaseTileData } from './tileSchemas'
import { type Tile } from 'components/TilesProvider/TilesProvider.types'
import {
  type TileCropAdjustmentContext,
  type TilePersistenceDisposition,
} from './BaseTileStore'
import { type TilePatchMode } from '../TilePatch'
import { type CropAlignment } from 'utils/cropAlignment.consts'

const SAVE_TILE_KEYS = ['width', 'height', 'useLowQualityAnyway', 'uid']
const SINGLE_PHOTO_PATCHABLE_KEYS_WHITELIST = new Set([
  'uid',
  'useLowQualityAnyway',
])

export class SinglePhotoTileStore extends BaseTileStore {
  @observable accessor uid: string | undefined

  @observable accessor useLowQualityAnyway: boolean | undefined

  readonly initialCropAlignment: CropAlignment | undefined

  constructor(data: SinglePhotoBaseTileData, photoBankStore: PhotoBankStore) {
    super(data, photoBankStore)

    this.uid = data.uid
    this.useLowQualityAnyway = data.useLowQualityAnyway ?? false
    this.initialCropAlignment = data.initialCropAlignment
    makeObservable(this)
  }

  @computed
  get photo(): Photo | undefined {
    if (!this.uid) return undefined
    return this.photoBankStore.getPhoto(this.uid)
  }

  @computed
  get width(): number {
    return this.photo?.width ?? 0
  }

  @computed
  get height(): number {
    return this.photo?.height ?? 0
  }

  @computed
  get isLocal(): boolean {
    return this.photo?.isLocal ?? true
  }

  @computed
  get url(): string | undefined {
    return this.photo?.url
  }

  @computed
  get minimalResUrl(): string | undefined {
    return this.photo?.minimalResUrl
  }

  @computed
  get lowResUrl(): string | undefined {
    return this.photo?.lowResUrl
  }

  @computed
  get highResUrl(): string | undefined {
    return this.photo?.highResUrl
  }

  @computed
  get thumbDimensions(): ThumbDimensions | undefined {
    return this.photo?.thumbDimensions
  }

  @computed
  override get originalUrl(): string {
    return this.photo?.url ?? ''
  }

  get tileContent(): TILE_CONTENT {
    return TILE_CONTENT.PHOTO
  }

  override getPhotoUids(): string[] {
    return this.uid ? [this.uid] : []
  }

  override hasValidRequiredPhotos(): boolean {
    return !!this.uid && !isExpiredPhoto(this.photo)
  }

  override getPersistenceState(): TilePersistenceDisposition {
    if (!this.uid) {
      return 'skip'
    }

    return this.isLocal ? 'pending' : 'ready'
  }

  override getCropAdjustmentContext(): TileCropAdjustmentContext | null {
    if (!this.width || !this.height) {
      return null
    }

    return {
      uid: this.uid,
      width: this.width,
      height: this.height,
      initialCropAlignment: this.initialCropAlignment,
    }
  }

  override toTileData(): SinglePhotoBaseTileData {
    return {
      ...this.baseTileData(),
      tileContent: TILE_CONTENT.PHOTO,
      uid: this.uid,
      width: this.width,
      height: this.height,
      useLowQualityAnyway: this.useLowQualityAnyway,
      initialCropAlignment: this.initialCropAlignment,
    }
  }

  override get persistenceKeys(): string[] {
    return super.persistenceKeys.concat(SAVE_TILE_KEYS)
  }

  protected override getPatchableKeysWhitelist(
    mode: TilePatchMode
  ): Set<string> {
    return new Set([
      ...super.getPatchableKeysWhitelist(mode),
      ...SINGLE_PHOTO_PATCHABLE_KEYS_WHITELIST,
    ])
  }

  protected override applyOriginalUrlPatch(
    _value: string | undefined,
    _mode: TilePatchMode
  ): boolean {
    return true
  }

  protected override _buildProviderSnapshot(): Tile {
    const result = super._buildProviderSnapshot()
    if (this.photo) {
      const photo = toJS(this.photo)
      if (!photo.isLocal) {
        const { uploadedAt: _uploadedAt, ...photoWithoutUploadedAt } = photo
        Object.assign(result, photoWithoutUploadedAt)
      } else {
        Object.assign(result, photo)
      }
    }
    return result
  }
}
